/* Page chrome for the results panel (nav bar, question detail card, grade section). Per-question-
type answer-rendering styles live in ./question-types/QuestionAnswer.module.css instead. */

/* ── Panel ─────────────────────────────────────────────────────── */
.panel {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
}

.emptyState {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	min-height: 200px;
	color: #6b7280;
	font-size: 0.875rem;
}

/* ── Navigator ─────────────────────────────────────────────────── */
.navBar {
	display: flex;
	align-items: center;
	border-top: 1px solid #f3f4f6;
	border-bottom: 1px solid #f3f4f6;
	padding: 6px 20px;
	background: white;
	flex-shrink: 0;
	gap: 20px;
}

.navBtn {
	width: 32px;
	height: 32px;
	border: 1px solid #e5e7eb;
	background-color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	color: #4b5563;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid var(--focus-outline-color-dark);
		outline-offset: 2px;
	}
}

.navScroll {
	display: flex;
	gap: 15px;
	overflow-x: auto;
	flex: 1;
	scrollbar-width: none;
	padding: 2px;
}

.navScroll::-webkit-scrollbar {
	display: none;
}

.navItem {
	/* reset browser button styles */
	appearance: none;
	font: inherit;
	/* layout */
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	cursor: pointer;
	flex-shrink: 0;
	background: transparent;
	border: 1.5px solid transparent;
	padding: 0;
	user-select: none;

	& .navLabel {
		font-size: 15px;
		line-height: 1.5;
		color: #6b7280;
	}

	&.active .navIconWrapper {
		outline: 3px solid var(--focus-outline-color-dark);
		outline-offset: -2px;
	}

	&.active .navLabel {
		color: var(--focus-outline-color-dark);
		font-weight: 700;
	}

	&:focus-visible {
		outline: 2px solid var(--focus-outline-color-dark);
		outline-offset: 0px;
	}
}

.navIconWrapper {
	position: relative;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #344054;
	border: 1px solid #e5e7eb;
	border-radius: 50%;
	background-color: #fff;

	& svg {
		width: 20px;
		height: auto;
	}
}

.navStatusOverlay {
	position: absolute;
	bottom: -5px;
	right: -5px;
	line-height: 0;
	& svg {
		width: 18px;
		height: auto;
	}
}

/* ── Detail ─────────────────────────────────────────────────────── */
.detailScroll {
	flex: 1;
	overflow: auto;
}

.detail {
	display: flex;
	flex-direction: column;
	max-width: 100%;
	padding: 15px;
	margin: 15px;
}

.questionDetailWrapper {
	display: flex;
	flex-direction: column;
	gap: 15px;
}

.detailHeader {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;

	& .questionTitle {
		font-size: 18px;
		font-weight: 600;
		color: #1f2937;
		margin: 0;
	}

	& .questionTypeLabel {
		font-size: 14px;
		font-weight: 400;
		color: #6b6b6b;
	}
}

.detailHeaderLeft {
	display: flex;
	align-items: center;
	gap: 10px;
}

.spacer {
	flex: 1;
}

.questionTextWrapper {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.answerSection {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 10px;
}

.sectionLabel:global(.MuiTypography-root) {
	font-size: 15px;
	font-weight: 600;
	margin-bottom: 0;
	color: #42296e;
}

.questionText:global(.MuiTypography-root) {
	line-height: 1.5;
	font-size: 15px;
	color: #1f2937;
	margin: 0;

	& p {
		margin: 0;
	}
}

/* ── AI Grading ────────────────────────────────────────────────────── */
.aiGradingBadge {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 2px 6px;

	border-radius: 4px;
	background-color: #633bee;

	p {
		font-family: Inter;
		font-weight: 500;
		font-size: 12px;
		line-height: 100%;
		letter-spacing: -0.2px;

		color: #ffffff;
	}
}

.viewGradingGuideline {
	width: fit-content;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 5px;
	padding: 0px 2px;
	cursor: pointer;
	border: 0;
	border-radius: 4px;
	background-color: transparent;
	outline: none;

	&:focus-visible {
		outline: 2px solid #0b6f85;
		outline-offset: 2px;
	}

	p {
		font-family: Inter;
		font-weight: 500;
		font-size: 14px;
		line-height: 100%;
		letter-spacing: 0px;
		vertical-align: middle;

		color: #42296e;
	}

	&:hover p {
		text-decoration: underline;
	}
}

/* ── Grade Section ─────────────────────────────────────────────────── */
.gradeSection {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	width: 100%;
}

.gradeHeader {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	width: 100%;

	& .gradeDescription {
		color: #6b7280;
		font-weight: 400;
		line-height: 1.6;
		font-size: 0.875rem;
	}
}

.gradeInputWrapper {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	gap: 0.5rem;
}

.gradeRadioGroup {
	margin-left: 0.25rem;
}
